import { create } from 'event-emitter';
import type { ChangeContext } from '../client/changeManager';
import type { DevtoolsCallbacks } from '../devtools/maybeLoadDevtools';
import type { Logger } from '../devtools/logger';
import { calculateTags } from '../links/discovery/calculateTags';
import { parseTypeName } from '../resources/resource';
import type { ResourceRegistry } from '../resources/resourceRegistry';
import type { CacheEntry } from './entries/cacheEntry';
import { establishEdge, type SubscriptionContext } from './establishEdge';
import type { NodeStore } from './nodeStore';

type TagBusEvent = {
  type: 'arrive' | 'depart';
  instanceKey: string;
  context: unknown;
  changeContext: ChangeContext;
};

type TagMatch = {
  instanceKey: string;
  neighborKey: string;
  tag: string;
  changeContext: ChangeContext;
  myContext: unknown;
  theirContext: unknown;
};

const tagChannel = (linkKey: string, tag: string) => `${linkKey}::${tag}`;

const breakerKey = (a: string, b: string, tag: string) => {
  const [first, second] = a < b ? [a, b] : [b, a];
  return `link::${first}::${second}::${tag}`;
};

export const createLinkManager = ({
  nodeStore,
  resourceRegistry,
  onInvalidated,
  onChange,
  schedule,
  scheduleInvalidation,
  logger,
  devtools,
}: {
  nodeStore: NodeStore;
  resourceRegistry: ResourceRegistry;
  onInvalidated: (instanceKey: string, changeContext: ChangeContext) => void;
  schedule: (effect: () => void) => void;
  scheduleInvalidation: (effect: () => void) => void;
  onChange: (
    instanceKey: string,
    entry: CacheEntry<any, any>,
    meta: {
      dataChanged: boolean;
      flagsChanged: boolean;
    }
  ) => void;
  logger?: Logger;
  devtools?: DevtoolsCallbacks;
}) => {
  const breaker = create<{ [key: string]: void }>();
  const tagBus = create<{ [channel: string]: TagBusEvent }>();
  const tagPresenceTeardown = create<{ [key: string]: ChangeContext }>();

  function syncTags(instanceKey: string, changeContext: ChangeContext) {
    const node = nodeStore.get(instanceKey);

    if (!node) {
      return;
    }

    tagPresenceTeardown.emit(instanceKey, changeContext);

    const typeName = parseTypeName(instanceKey);
    const hasData = node.state.data !== undefined;

    const dataResult = hasData
      ? calculateTags({
          typeName,
          params: node.meta.params,
          data: node.state.data,
          resourceRegistry,
        })
      : null;

    const paramTags = node.meta.paramLinkTags;
    const dataTags = dataResult?.tags ?? null;

    // Retain the data-derived tags + per-tag contexts on the node. Computed
    // here every change anyway; link propagation reads it back to reconstruct
    // the full set of matched contexts between two instances (see establishEdge),
    // and it's dropped automatically when the entry is GC'd.
    node.meta.dataLinkTags = dataResult;

    if (!paramTags && !dataTags) {
      return;
    }

    const processTag = (linkKey: string, tag: string) => {
      const channel = tagChannel(linkKey, tag);
      const myContext = dataResult?.contexts?.get(linkKey, tag);

      const arrivalEvent: TagBusEvent = {
        type: 'arrive',
        instanceKey,
        context: myContext,
        changeContext,
      };

      tagBus.emit(channel, arrivalEvent);

      const busOff = tagBus.on(channel, (event) => {
        if (event.type === 'arrive') {
          handleMatch({
            instanceKey,
            neighborKey: event.instanceKey,
            tag,
            changeContext: event.changeContext,
            myContext,
            theirContext: event.context,
          });
        } else {
          breaker.emit(breakerKey(instanceKey, event.instanceKey, tag));
          devtools?.onLinkRemoved?.(
            instanceKey,
            event.instanceKey,
            event.changeContext
          );
        }
      });

      const teardownOff = tagPresenceTeardown.on(
        instanceKey,
        (removalContext: ChangeContext) => {
          busOff();
          tagBus.emit(channel, {
            ...arrivalEvent,
            type: 'depart',
            changeContext: removalContext,
          });
          devtools?.onTagRemoved?.(instanceKey, linkKey, tag, removalContext);
          teardownOff();
        }
      );

      devtools?.onTagAdded?.(instanceKey, linkKey, tag, changeContext);
    };

    if (paramTags) {
      for (const [linkKey, tag] of paramTags) {
        processTag(linkKey, tag);
      }
    }

    if (dataTags) {
      for (const [linkKey, tag] of dataTags) {
        processTag(linkKey, tag);
      }
    }
  }

  const subscriptionContext: SubscriptionContext = {
    nodeStore,
    schedule,
    scheduleInvalidation,
    onInvalidated,
    onChange,
    logger,
    devtools,
  };

  function handleMatch({
    instanceKey,
    neighborKey,
    tag,
    changeContext,
    myContext,
    theirContext,
  }: TagMatch) {
    if (instanceKey === neighborKey) {
      return;
    }

    const myType = parseTypeName(instanceKey);
    const neighborType = parseTypeName(neighborKey);
    const edge = resourceRegistry.getLinkInfo(myType, neighborType);

    if (!edge) {
      return;
    }

    const myHash = resourceRegistry.getResourceFromTypeName(myType).hash;
    const theirHash =
      resourceRegistry.getResourceFromTypeName(neighborType).hash;

    const cleanupOutgoing = establishEdge(
      subscriptionContext,
      neighborKey,
      instanceKey,
      edge.outgoing,
      theirHash,
      myContext,
      theirContext
    );
    const cleanupIncoming = establishEdge(
      subscriptionContext,
      instanceKey,
      neighborKey,
      edge.incoming,
      myHash,
      theirContext,
      myContext
    );

    const breakerOff = breaker.on(
      breakerKey(instanceKey, neighborKey, tag),
      () => {
        cleanupOutgoing();
        cleanupIncoming();
        breakerOff();
      }
    );

    devtools?.onLinkAdded?.(
      instanceKey,
      neighborKey,
      edge.outgoing.behavior,
      edge.incoming.behavior,
      changeContext
    );
  }

  return {
    syncTags,
    removeInstance: (instanceKey: string, changeContext: ChangeContext) => {
      tagPresenceTeardown.emit(instanceKey, changeContext);
    },
  };
};

export type LinkManager = ReturnType<typeof createLinkManager>;
